/**
 * cs-header-scroll-theme.js
 *
 * Scroll-aware light/dark theme switcher for the transparent header (.hdr-trans-hp).
 *
 * How it works:
 *   On every scroll and resize event, samples the element directly below the header
 *   using document.elementsFromPoint at the horizontal centre of the page.
 *   It walks up each element's ancestor chain computing relative luminance (WCAG formula)
 *   from the computed background-color. If luminance > 0.179 the section is "light",
 *   otherwise "dark". The result is written as data-scroll-theme="light|dark" on
 *   #SiteHeader, which CSS uses to flip text color, logo variant, and other vars.
 *
 * Manual override:
 *   Add data-header-theme="light" or data-header-theme="dark" to any section element
 *   to skip the luminance calculation and force a specific theme for that section.
 *
 * Performance:
 *   Scroll handler is rAF-throttled (ticking flag). Both listeners use { passive: true }.
 *
 * Dependencies:
 *   - #SiteHeader must have class .hdr-trans-hp — exits early if not present.
 *   - CSS vars --color-menu-text toggled in component-header.custom.css.
 */
(function () {
  const header = document.getElementById('SiteHeader');
  if (!header || !header.classList.contains('hdr-trans-hp')) return;

  /**
   * WCAG relative luminance for an sRGB channel value 0-255.
   * Linearises the channel then weights R/G/B by their luminance coefficients.
   */
  function luminance(r, g, b) {
    return [r, g, b]
      .map((c) => {
        c /= 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      })
      .reduce((acc, c, i) => acc + c * [0.2126, 0.7152, 0.0722][i], 0);
  }

  /**
   * Returns numeric luminance for el's computed background-color, considering alpha.
   * Treats translucent/transparent layers as null so caller can keep walking ancestors.
   */
  function bgLuminance(el) {
    const style = window.getComputedStyle(el);
    const bg = style.backgroundColor;
    const nums = bg.match(/[\d.]+/g);
    if (!nums || nums.length < 3) return null;
    const [r, g, b, a = 1] = nums.map(Number);
    if (a < 0.5) return null;
    return luminance(r, g, b);
  }

  function themeFromLuminance(L) {
    return L > 0.55 ? 'light' : 'dark';
  }

  /** Writes data-scroll-theme only when the value actually changes (avoids repaints). */
  function setTheme(theme) {
    if (header.getAttribute('data-scroll-theme') !== theme) {
      header.setAttribute('data-scroll-theme', theme);
    }
  }

  /**
   * Samples the element stack at the centre-bottom of the header.
   * Priority order:
   *   1. data-header-theme attribute on the element — manual override.
   *   2. Computed background-color walking up the DOM tree.
   *   3. Any background-image detected → assumes dark (images are typically rich/dark).
   *   4. Fallback: dark.
   */
  const MEDIA_TAGS = new Set(['IMG', 'PICTURE', 'VIDEO', 'CANVAS', 'svg', 'SVG']);

  function sampleAt(x, y) {
    const stack = document.elementsFromPoint(x, y);
    for (const el of stack) {
      if (header.contains(el) || el === document.documentElement || el === document.body) continue;
      const overrideAncestor = el.closest && el.closest('[data-header-theme]');
      if (overrideAncestor && !header.contains(overrideAncestor)) return { override: overrideAncestor.dataset.headerTheme };

      const isMedia = MEDIA_TAGS.has(el.tagName);
      let node = el;
      while (node && node !== document.body) {
        if (node.dataset && node.dataset.headerTheme) return { override: node.dataset.headerTheme };
        const L = bgLuminance(node);
        if (L !== null) return { L };
        if (window.getComputedStyle(node).backgroundImage !== 'none') return { L: 0 };
        node = node.parentElement;
      }
      if (isMedia) return { L: 0 };
    }
    return null;
  }

  function updateScrollTheme() {
    const rect = header.getBoundingClientRect();
    const y = rect.bottom + 2;
    const xs = [
      rect.left + rect.width * 0.15,
      rect.left + rect.width * 0.3,
      rect.left + rect.width * 0.5,
      rect.left + rect.width * 0.7,
      rect.left + rect.width * 0.85,
    ];

    let total = 0;
    let count = 0;
    for (const x of xs) {
      const s = sampleAt(x, y);
      if (!s) continue;
      if (s.override) { setTheme(s.override); return; }
      total += s.L;
      count += 1;
    }

    if (count === 0) { setTheme('dark'); return; }
    setTheme(themeFromLuminance(total / count));
  }

  /** rAF-throttled scroll handler — prevents layout thrashing on fast scrolls. */
  let ticking = false;
  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(() => { updateScrollTheme(); ticking = false; });
      ticking = true;
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', updateScrollTheme, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateScrollTheme);
  } else {
    updateScrollTheme();
  }
})();
